<template>
  <nav v-if="$beet.isReady" class="navbar">
    <LangRouterLink :to="{ name: 'home' }" @click.native="isMobileNavOpen = false">
      <img src="@/assets/images/logo.svg" alt="CRH" class="navbar__logo" />
    </LangRouterLink>

    <ul class="navbar__content -center" @mouseleave="closeDropDown">
      <li v-for="(_, key) in $store.state.navigation.main" :key="key" class="navbar__link">
        <LangRouterLink
          :to="{ name: routes(key) }"
          class="navbar__link-label"
          @mouseenter.native="setDropDownContent(key)"
          @click.native="closeDropDown"
        >
          {{ $beet.options.menu[`${key}Title`] }}
        </LangRouterLink>
      </li>

      <DropDown tag="li" :is-open="!!dropDownContent" :class="dropDownClassName">
        <Transition name="tray" mode="out-in" :duration="450">
          <div v-if="dropDownContent" :key="dropDownContentKey" class="tray__content">
            <div class="tray__text">
              <h4 class="tray__title">{{ $beet.options.menu[`${dropDownContentKey}Title`] }}</h4>
              <p class="tray__description">{{ $beet.options.menu[`${dropDownContentKey}Text`] }}</p>
              <LangRouterLink :to="{ name: 'post.index' }">
                <div v-if="dropDownContent.linkLabel" class="tray__link -blue">
                  <Icon id="arrow" class="tray__icon" />
                  <span>{{ dropDownContent.linkLabel }}</span>
                </div>
              </LangRouterLink>
            </div>

            <nav v-if="dropDownContentKey === 'blog'" class="tray__nav -blog">
              <LangRouterLink
                v-for="(post, i) in $store.getters.latest_posts"
                :key="`nav-post-${post.id}`"
                :to="{ name: 'post.show', params: { slug: `${post.slug}-${post.id}` } }"
                class="tray__article"
                :style="getTransitionDelay(i)"
                @click.native="closeDropDown"
              >
                <div>
                  <h2>{{ post.title }}</h2>
                  <p>{{ formatDate(post.published_at, $i18n.locale) }}</p>
                </div>
              </LangRouterLink>
            </nav>

            <nav v-else-if="dropDownContentKey" class="tray__nav">
              <template v-for="(key, i) in dropDownContent" :key="key">
                <LangRouterLink
                  v-if="key !== 'scholarships'"
                  :to="{
                    name: routes(dropDownContentKey),
                    hash: `#${$t(`navigation.anchors.${dropDownContentKey}.${key}`)}`,
                  }"
                  class="tray__link"
                  :style="getTransitionDelay(i)"
                  @click.native="closeDropDown"
                >
                  <NonBreakingEnd
                    :text="$t(`navigation.labels.${dropDownContentKey}.${key}`)"
                    punctuation-only
                  />
                </LangRouterLink>
                <a
                  v-else-if="scholarshipsLink"
                  :href="scholarshipsLink"
                  target="_blank"
                  class="tray__link"
                  :style="getTransitionDelay(i)"
                  @click.native="closeDropDown"
                >
                  {{ $t(`navigation.labels.${dropDownContentKey}.${key}`) }}
                </a>
              </template>
            </nav>

            <figure
              class="tray__image"
              :style="{
                backgroundImage: `url('${$beet.options.menu[`${dropDownContentKey}Img`].original}')`,
              }"
            />
          </div>
        </Transition>
      </DropDown>
    </ul>

    <div class="navbar__content">
      <LangRouterLink :to="{ name: 'contact', hash: '#map' }">
        <span class="button -link -border navbar__button --desktop">{{
          $t('find_a_location')
        }}</span>
        <Icon id="map-pin" class="navbar__icon --mobile" />
      </LangRouterLink>

      <LangRouterLink :to="{ name: 'contact' }">
        <span class="button -link navbar__button --desktop">{{ $t('contact_us') }}</span>
        <Icon id="mail" class="navbar__icon --mobile" />
      </LangRouterLink>

      <LangSwitchLink />
    </div>

    <div class="navbar__content -mobile">
      <LangRouterLink
        :to="{ name: 'contact', hash: '#map' }"
        class="navbar__icon"
        @click.native="isMobileNavOpen = false"
      >
        <Icon id="map-pin" />
      </LangRouterLink>

      <LangRouterLink
        :to="{ name: 'contact' }"
        class="navbar__icon"
        @click.native="isMobileNavOpen = false"
      >
        <Icon id="mail" />
      </LangRouterLink>

      <button
        :class="['navbar__burger', { '-close': isMobileNavOpen }]"
        @click="isMobileNavOpen = !isMobileNavOpen"
      >
        <span class="navbar__burger-line" />
      </button>
    </div>
  </nav>
  <MobileNav :is-open="isMobileNavOpen" @close="isMobileNavOpen = false" />
</template>

<script>
import DropDown from '@/components/global/DropDown.vue'
import Icon from '@/components/global/Icon.vue'
import LangRouterLink from '@/components/global/LangRouterLink.vue'
import NonBreakingEnd from '@/components/global/NonBreakingEnd.vue'
import LangSwitchLink from '@/components/nav/LangSwitchLink.vue'
import MobileNav from '@/components/nav/MobileNav.vue'
import { formatDate } from '@/helpers/dateHelpers.js'

export default {
  name: 'SiteNavbar',
  components: { LangSwitchLink, MobileNav, DropDown, Icon, LangRouterLink, NonBreakingEnd },
  setup: () => ({ formatDate, scholarshipsLink: import.meta.env.VITE_APP_SCHOLARSHIPS_PAGE_URL }),
  data() {
    return {
      isMobileNavOpen: false,
      dropDownContentKey: null,
    }
  },
  computed: {
    dropDownClassName() {
      return ['tray', { '-open': !!this.dropDownContent }]
    },
    dropDownContent() {
      return this.dropDownContentKey
        ? this.$store.state.navigation.main[this.dropDownContentKey]
        : null
    },
  },
  methods: {
    setDropDownContent(key) {
      this.dropDownContentKey = key
    },
    closeDropDown() {
      this.dropDownContentKey = null
    },
    getTransitionDelay(index) {
      return { transitionDelay: `${150 + 90 * index}ms` }
    },
    routes(key) {
      switch (key) {
        case 'products':
          return 'product.index'
        case 'blog':
          return 'post.index'
        default:
          return key
      }
    },
  },
}
</script>
